import { CfbInput, CfbScores } from './cfb-client';
import { CfbSnapshot } from './cfb-workspace';

// DNS names are case-insensitive; accept one terminal root dot, never suffixes or URLs.
const normalizedHostname = (hostname: string) => hostname.toLowerCase().replace(/\.$/, '');

export function isCfbFixtureEnabled(production: boolean, hostname: string): boolean {
  return !production && ['127.0.0.1', 'localhost'].includes(normalizedHostname(hostname));
}

export function isCfbEnabled(production: boolean, hostname: string, projectId: string, hostedEnabled: boolean): boolean {
  return isCfbFixtureEnabled(production, hostname) || (!production && hostedEnabled && projectId === 'acemind---dev'
    && ['dev.acemind.io', 'acemind---dev.web.app', 'acemind---dev.firebaseapp.com'].includes(normalizedHostname(hostname)));
}

export function cfbStorageKey(projectId: string, uid: string, fixture: boolean): string {
  return `acemind.cfb.${fixture ? 'fixture' : 'hosted'}.${encodeURIComponent(projectId)}.${encodeURIComponent(uid)}.v1`;
}

export function cfbEntryBuilderRoute(sessionId: string, owner = 'peter'): string[] {
  return ['/results', 'cfb', owner, sessionId];
}

/** Presentation rows only: never substitute table offsets for adapter candidate IDs. */
export function cfbEntryTable(snapshot: CfbSnapshot | undefined, input: CfbInput | undefined) {
  const columns = [
    { name: 'Candidate', prop: 'candidateLabel', disableSort: true },
    ...(snapshot?.slots || []).map((name, index) => ({ name, prop: `slot${index}`, disableSort: true })),
    { name: 'Projection', prop: 'projectedPoints', disableSort: true },
    { name: 'Salary', prop: 'salary', disableSort: true },
    { name: 'Entries', prop: 'entryCount', disableSort: true, entryCount: true },
  ];
  const players = new Map(input?.players.map(player => [player.sitePlayerId, player]) || []);
  const rows = (snapshot?.rows || []).map(candidate => ({
    ...candidate,
    candidateLabel: candidate.candidateId.slice(0, 10),
    entryCount: snapshot?.counts[candidate.candidateId] || 0,
    ...Object.fromEntries(candidate.sitePlayerIds.map((id, index) =>
      [`slot${index}`, `${players.get(id)?.name || id} (${id})`])),
  }));
  return { columns, rows };
}

/** Stored per-entry metrics; multiplicity is displayed separately, never multiplied into probabilities. */
export function cfbScoreTable(scores: CfbScores | undefined) {
  const percent = (value: number | null) => value === null ? 'N/A' : `${(value * 100).toFixed(2)}%`;
  const columns = [
    { name: 'Candidate', prop: 'candidateId', disableSort: true },
    ...(scores?.slotColumns || []).map((name, index) => ({ name, prop: `slot${index}`, disableSort: true })),
    ...[['Entries', 'count'], ['Mean gross payout', 'payout'], ['ROI', 'roiLabel'],
      ['Win tie credit', 'win'], ['Profit probability', 'cash'], ['Top 1%', 'top'],
      ['Mean rank', 'rank'], ['Duplicates', 'dupes']].map(([name, prop]) => ({ name, prop, disableSort: true })),
  ];
  const rows = (scores?.rows || []).map(row => ({ ...row,
    ...Object.fromEntries(row.sitePlayerIds.map((id, index) => [`slot${index}`, id])),
    payout: row.mean_payout.toFixed(2), roiLabel: percent(row.roi), win: percent(row.p_win),
    cash: percent(row.p_cash), top: percent(row.p_top1pct), rank: row.mean_rank.toFixed(2),
  }));
  return { columns, rows };
}

/** UI preparation only. Real execution stays closed until backend contracts are approved. */
export type CfbBuilderMode = 'simulator' | 'optimizer';
export type CfbBuilderSource = 'real' | 'synthetic';
export function cfbCanUseSynthetic(mode: string, source: string): boolean {
  return mode === 'optimizer' && source === 'synthetic';
}

export { cfbExecutionMode } from './cfb-input-selection';
